export interface MessageTemplates {
  email_subject: string;
  email_body: string;
  whatsapp_message: string;
  gym_checkin_message: string;
  gym_checkout_message: string;
  canteen_slot_message: string;
  weekly_report_whatsapp: string;
  qr_badge_whatsapp: string;
  membership_payment_link: string;
  membership_expired: string;
  membership_expiring: string;
  employee_checkin: string;
  employee_checkout: string;
}

export const DEFAULT_TEMPLATES: MessageTemplates = {
  email_subject: 'Invoice #{invoice} from {company}',
  email_body: `Dear {name},\n\nPlease find your invoice #{invoice} for {currency}{amount}.\n\nDate: {date}\n\nThank you for your business!`,
  whatsapp_message: `Hi {name}! 👋\n\n📄 Invoice #{invoice}\n💰 Amount: {currency}{amount}\n📅 Date: {date}\n\nThank you! 🙏`,
  gym_checkin_message: `✅ *Check-In Recorded*\n\nHi {name}! You have entered the gym at *{time}* on {date}.\n\n💪 Keep it up!\n— {company}`,
  gym_checkout_message: `🚪 *Check-Out Recorded*\n\nHi {name}! You have exited the gym at *{time}* on {date}.\n\nSee you next time! 💪\n— {company}`,
  canteen_slot_message: `✅ *{slot} Attendance Marked*\n\nHi {name}! Your {slot} slot has been recorded at *{time}* on {date}.\n\n— {company}`,
  weekly_report_whatsapp: `{icon} *Weekly {module} Report*\n\nHi {name}!\n\nHere's your attendance summary for this week:\n\n{dayRows}\n\n📊 *Summary*\n✅ Attended: {attended}/{total} slots\n📈 Attendance: {pct}%{missedLine}\n\n{encouragement}\n\n— {company}`,
  qr_badge_whatsapp: `{icon} *Your {module} QR Badge*\n\nHi {name}! Here is your personal QR code.\n\nShow this QR when you arrive — staff will scan it to mark your attendance.\n\n📋 Plan: {plan}\n📅 Valid: {start} → {end}\n🕐 Slots: {slots}\n\n— {company}`,
  membership_payment_link: `💳 *Membership Payment*\n\nHi {name}! Please pay your membership fee of {currency}{amount} using the secure link below:\n\n{link}\n\nPlan: {plan}\nValid: {start} → {end}\n\n— {company}`,
  membership_expired: `{icon} *Membership Expired*\n\nHi {name}! Your {module} membership ({plan}) expired on *{end}*.\n\nRenew now to continue enjoying our services!\n\n— {company}`,
  membership_expiring: `{icon} *Membership Expiring Soon*\n\nHi {name}! Your {module} membership ({plan}) is expiring on *{end}*.\n\nRenew before it expires to avoid interruption!\n\n— {company}`,
  employee_checkin: `✅ *Check-In Recorded*\n\nHi {name}, your check-in has been recorded at {time} on {date}.\n\n— {company}`,
  employee_checkout: `👋 *Check-Out Recorded*\n\nHi {name}, your check-out has been recorded at {time} on {date}.\n\n— {company}`,

};

const BASE = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

export async function loadMessageTemplates(outletId?: number | null): Promise<MessageTemplates> {
  try {
    const jwt = localStorage.getItem('cloudairambo_jwt') || '';
    const url = outletId != null
      ? `${BASE}/outlets/${outletId}/message-templates`
      : `${BASE}/user/message-templates`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${jwt}` },
    });
    if (!res.ok) return { ...DEFAULT_TEMPLATES };
    const data = await res.json();
    return {
      email_subject: data.email_subject || DEFAULT_TEMPLATES.email_subject,
      email_body: data.email_body || DEFAULT_TEMPLATES.email_body,
      whatsapp_message: data.whatsapp_message || DEFAULT_TEMPLATES.whatsapp_message,
      gym_checkin_message: data.gym_checkin_message || DEFAULT_TEMPLATES.gym_checkin_message,
      gym_checkout_message: data.gym_checkout_message || DEFAULT_TEMPLATES.gym_checkout_message,
      canteen_slot_message: data.canteen_slot_message || DEFAULT_TEMPLATES.canteen_slot_message,
      weekly_report_whatsapp: data.weekly_report_whatsapp || DEFAULT_TEMPLATES.weekly_report_whatsapp,
      qr_badge_whatsapp: data.qr_badge_whatsapp || DEFAULT_TEMPLATES.qr_badge_whatsapp,
      membership_payment_link: data.membership_payment_link || DEFAULT_TEMPLATES.membership_payment_link,
      membership_expired: data.membership_expired || DEFAULT_TEMPLATES.membership_expired,
      membership_expiring: data.membership_expiring || DEFAULT_TEMPLATES.membership_expiring,
      employee_checkin: data.employee_checkin || DEFAULT_TEMPLATES.employee_checkin,
      employee_checkout: data.employee_checkout || DEFAULT_TEMPLATES.employee_checkout,
    
    };
  } catch {
    return { ...DEFAULT_TEMPLATES };
  }
}

export async function saveMessageTemplates(templates: MessageTemplates, outletId?: number | null): Promise<void> {
  const jwt = localStorage.getItem('cloudairambo_jwt') || '';
  const url = outletId != null
    ? `${BASE}/outlets/${outletId}/message-templates`
    : `${BASE}/user/message-templates`;
  await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` },
    body: JSON.stringify(templates),
  });
}

export function applyTemplate(
  template: string,
  vars: {
    name?: string;
    invoice?: string;
    amount?: string | number;
    currency?: string;
    date?: string;
    company?: string;
    phone?: string;
    email?: string;
    time?: string;
    slot?: string;
    module?: string;
    icon?: string;
    plan?: string;
    start?: string;
    end?: string;
    slots?: string;
    dayRows?: string;
    attended?: string | number;
    total?: string | number;
    pct?: string | number;
    missedLine?: string;
    encouragement?: string;
    link?: string;
    scan_type?: string;
  }
): string {
  return template
    .replace(/\{name\}/g, vars.name || 'Customer')
    .replace(/\{invoice\}/g, vars.invoice || '')
    .replace(/\{amount\}/g, typeof vars.amount === 'number' ? vars.amount.toFixed(2) : (vars.amount || '0.00'))
    .replace(/\{currency\}/g, vars.currency || '')
    .replace(/\{date\}/g, vars.date || new Date().toLocaleDateString())
    .replace(/\{company\}/g, vars.company || '')
    .replace(/\{phone\}/g, vars.phone || '')
    .replace(/\{email\}/g, vars.email || '')
    .replace(/\{time\}/g, vars.time || '')
    .replace(/\{slot\}/g, vars.slot || '')
    .replace(/\{module\}/g, vars.module || '')
    .replace(/\{icon\}/g, vars.icon || '')
    .replace(/\{plan\}/g, vars.plan || '')
    .replace(/\{start\}/g, vars.start || '')
    .replace(/\{end\}/g, vars.end || '')
    .replace(/\{slots\}/g, vars.slots || '')
    .replace(/\{dayRows\}/g, vars.dayRows || '')
    .replace(/\{attended\}/g, String(vars.attended ?? ''))
    .replace(/\{total\}/g, String(vars.total ?? ''))
    .replace(/\{pct\}/g, String(vars.pct ?? ''))
    .replace(/\{missedLine\}/g, vars.missedLine || '')
    .replace(/\{encouragement\}/g, vars.encouragement || '')
    .replace(/\{link\}/g, vars.link || '')
    .replace(/\{scan_type\}/g, vars.scan_type || '');
}